import React, { useState, useRef, useCallback, useEffect } from "react";
import { Video, Square, FlipHorizontal2, Loader2, Camera } from "lucide-react";

export default function VideoRecorder({ onRecordingComplete, disabled = false, maxDuration = 120 }) {
  const [recording, setRecording] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [facingMode, setFacingMode] = useState("user");
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startPreview = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setPreviewing(true);
    } catch {
      // Camera not available
    }
  }, [facingMode]);

  const stopPreview = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setPreviewing(false);
  }, []);

  useEffect(() => {
    return () => {
      stopPreview();
      clearInterval(timerRef.current);
    };
  }, [stopPreview]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    
    // Try different mime types for cross-browser compatibility
    // iOS Safari: MP4/H.264, Chrome/Firefox: WebM/VP8 or VP9
    let mimeType = "video/webm;codecs=vp9,opus";
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = "video/webm;codecs=vp8,opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/webm";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "video/mp4";
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = ""; // Let browser choose default
          }
        }
      }
    }
    
    const options = mimeType ? { mimeType } : {};
    const mediaRecorder = new MediaRecorder(streamRef.current, options);
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      // Determine the actual mime type and extension
      const actualType = mediaRecorder.mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, { type: actualType });
      stopPreview();
      onRecordingComplete?.(blob);
      setDuration(0);
    };

    mediaRecorder.start();
    setRecording(true);
    setDuration(0);
    timerRef.current = setInterval(() => {
      setDuration((d) => {
        if (d + 1 >= maxDuration) {
          mediaRecorderRef.current?.stop();
          setRecording(false);
          clearInterval(timerRef.current);
        }
        return d + 1;
      });
    }, 1000);
  }, [maxDuration, onRecordingComplete, stopPreview]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  }, [recording]);

  const flipCamera = useCallback(() => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    if (previewing && !recording) {
      stopPreview();
      setTimeout(() => startPreview(), 100);
    }
  }, [facingMode, previewing, recording, stopPreview, startPreview]);

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="w-full" data-testid="video-recorder">
      {!previewing ? (
        <button
          onClick={startPreview}
          disabled={disabled}
          data-testid="start-camera-btn"
          className="w-full rounded-xl border-2 border-dashed border-white/10 hover:border-white/20 p-12 flex flex-col items-center gap-3 transition-colors duration-200"
        >
          <div className="w-16 h-16 rounded-full bg-[#00FF88]/10 flex items-center justify-center">
            <Camera className="w-7 h-7 text-[#00FF88]" />
          </div>
          <p className="text-sm text-slate-400">Click to open camera</p>
        </button>
      ) : (
        <div className="relative rounded-xl overflow-hidden bg-black">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full aspect-video object-cover"
            style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
          />

          {/* Recording indicator */}
          {recording && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs text-white font-mono tabular-nums" data-testid="video-duration">
                {formatDuration(duration)}
              </span>
              <span className="text-[10px] text-slate-400">/ {formatDuration(maxDuration)}</span>
            </div>
          )}

          {/* Controls */}
          <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
            <button
              onClick={flipCamera}
              disabled={recording}
              className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors disabled:opacity-30"
              data-testid="flip-camera-btn"
            >
              <FlipHorizontal2 className="w-4 h-4" />
            </button>

            <button
              onClick={recording ? stopRecording : startRecording}
              data-testid={recording ? "stop-video-btn" : "start-video-btn"}
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                recording
                  ? "bg-red-500 hover:bg-red-600 shadow-[0_0_25px_rgba(239,68,68,0.5)]"
                  : "bg-[#00FF88] hover:bg-[#00CC6A] shadow-[0_0_25px_rgba(0,255,136,0.4)]"
              }`}
            >
              {recording ? (
                <Square className="w-6 h-6 text-white" fill="white" />
              ) : (
                <Video className="w-6 h-6 text-black" />
              )}
            </button>

            <button
              onClick={() => { stopPreview(); }}
              className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              data-testid="close-camera-btn"
            >
              <span className="text-xs font-medium">ESC</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
